import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();
  #updateSequence = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Reads the currently selected option named "Farbe" from Fabric's picker.
   *
   * @param {Element} eventTarget
   * @returns {string}
   */
  #getSelectedColor(eventTarget) {
    const section = this.closest('.shopify-section, dialog');
    const picker =
      eventTarget.closest('variant-picker') ||
      section?.querySelector('variant-picker');

    if (!picker) return '';

    const selectedOptions = picker.getAllSelectedOptions?.() || [];
    const colorOption = selectedOptions.find(
      (option) => option.name?.trim().toLowerCase() === 'farbe'
    );

    return colorOption?.value?.trim().toLowerCase() || '';
  }

  /**
   * Filters the newly fetched gallery before it is inserted into the page.
   */
  #filterGalleryByColor(gallery, selectedColor) {
    if (!selectedColor) return;

    const mediaItems = Array.from(
      gallery.querySelectorAll(
        'slideshow-slide, .media-gallery__grid > li, .dialog-zoomed-gallery > li'
      )
    );

    const hasMatchingColor = mediaItems.some((item) => {
      const image = item.querySelector('img[alt]');
      const alt = image?.getAttribute('alt')?.trim().toLowerCase() || '';
      return alt === selectedColor;
    });

    if (!hasMatchingColor) return;

    mediaItems.forEach((item) => {
      const image = item.querySelector('img[alt]');
      const alt = image?.getAttribute('alt')?.trim().toLowerCase() || '';

      if (!alt || alt === selectedColor) return;

      item.remove();
    });

    gallery.querySelectorAll('slideshow-controls').forEach((controls) => {
      controls.querySelectorAll('button, [role="button"]').forEach((button) => {
        const image = button.querySelector('img[alt]');
        const alt = image?.getAttribute('alt')?.trim().toLowerCase() || '';

        if (alt && alt !== selectedColor) {
          button.remove();
        }
      });
    });

    gallery.querySelectorAll('.dialog-thumbnails-list > *').forEach((thumbnail) => {
      const image = thumbnail.querySelector('img[alt]');
      const alt = image?.getAttribute('alt')?.trim().toLowerCase() || '';

      if (alt && alt !== selectedColor) {
        thumbnail.remove();
      }
    });
  }

  /**
   * Handles a product select event.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('product-card')) return;

    const currentSequence = ++this.#updateSequence;
    const selectedColor = this.#getSelectedColor(event.target);

    event.promise
      .then(({ detail }) => {
        if (currentSequence !== this.#updateSequence) return;
        if (!detail?.html) return;

        const newMediaGallery = detail.html.querySelector('media-gallery');

        if (!newMediaGallery) {
          console.warn('[media-gallery] Keine neue Galerie gefunden.');
          return;
        }

        const hasMedia = newMediaGallery.querySelector(
          'slideshow-slide, .media-gallery__grid > li, [ref="media[]"]'
        );

        if (!hasMedia) {
          console.warn(
            '[media-gallery] Neue Galerie enthält keine Medien. Alte Galerie bleibt erhalten.'
          );
          return;
        }

        this.#filterGalleryByColor(newMediaGallery, selectedColor);

        const filteredHasMedia = newMediaGallery.querySelector(
          'slideshow-slide, .media-gallery__grid > li, [ref="media[]"]'
        );

        if (!filteredHasMedia) {
          console.warn(
            '[media-gallery] Keine Medien für diese Farbe gefunden. Alte Galerie bleibt erhalten.'
          );
          return;
        }

        if (currentSequence !== this.#updateSequence) return;
        if (!this.isConnected) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[media-gallery] Aktualisierung fehlgeschlagen:',
            error
          );
        }
      });
  };

  /**
   * Handles zoom media selection.
   *
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, {
      animate: false,
    });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   *
   * @param {number} index
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(
      zoomDialogMedia
    );
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}